import React from "react";
// disabling eslint resolution warning, as the @toaster/pantry/react
// package makes use of package.json exports
// which eslint does not support. We can add a third
// party module, but in this case, since this package is the only
// one affected, simply disabling the eslint rule
// eslint-disable-next-line import/no-unresolved
import { ToasterEmbed } from "@toaster/pantry/react";
import { createClasses } from "@bloomberg/media-ui-core";

import ThirdPartyEmbed from "~components/shared/third-party-embed/ThirdPartyEmbed";
import type { CuratedItem as HydratedCuratedItem } from "~types/types";

import styles from "../Media.module.css";
import { type MediaChildProps, MediaFormat } from "../utils/types";

/**
 * Exported validation method used by the Media Decider.
 * All Media subcomponents must expose this function.
 */
export function hasValidEmbeddedIframeData(
    source: HydratedCuratedItem,
    format: MediaFormat
): boolean {
    return (
        Boolean(source?.media?.embeddedIframe) &&
        format === MediaFormat.embeddedIframe
    );
}

export function extractToasterUuidFromUrl(url: string) {
    const pattern = /\/charts\/([^/]+?)(?:\.html|\?|$)/;
    const match = url.match(pattern);
    return match ? match[1] : null;
}

export function isToasterUrl(source?: string): boolean {
    if (!source) return false;

    if (
        source?.includes("/toaster/v2/charts/") ||
        source?.includes("/toaster/v1/charts/")
    ) {
        return true;
    }
    return false;
}

export function extractAnimationParamsFromUrl(source?: string): {
    [key: string]: string;
} {
    if (!source) return {};

    // Homepage charts often feature animations (aka datagrams),
    // so we need to pass through any animation parameters explicitly
    const animationParams: { [key: string]: string } = {};

    try {
        const url = new URL(source);

        for (const [key, value] of url.searchParams) {
            if (key.includes("transition")) {
                animationParams[key] = value;
            }
        }
    } catch {
        // Invalid URL, log the error
        // and then swallow it. this will
        // mean the chart does not animate, but that is ok
        // eslint-disable-next-line no-console
        console.error("Invalid Toaster Source URL:", source);
    }

    return animationParams;
}

export function EmbeddedIframeMedia({
    source,
    format,
    config = {},
}: MediaChildProps) {
    if (!source?.media?.embeddedIframe) return null;

    const embedUrl = source.media.embeddedIframe;

    const componentID = `media--${format}`;
    const containerClasses = createClasses(
        styles.Media,
        styles.embeddedIframe,
        [config.className && config.className]
    );

    const additionalQueryParams: Record<string, string> = {
        display: "fixed",
    };

    const title = `EmbeddedIframeMedia-${embedUrl}`;

    if (embedUrl && isToasterUrl(embedUrl)) {
        // extract uuid from url
        const uuid = extractToasterUuidFromUrl(embedUrl);
        const animationParams = extractAnimationParamsFromUrl(embedUrl);

        if (uuid) {
            return (
                <div className={containerClasses} data-component={componentID}>
                    <ToasterEmbed
                        id={uuid}
                        titles
                        logo={false}
                        src={embedUrl}
                        animationParams={animationParams}
                    />
                </div>
            );
        }
        return null;
    }

    return (
        <div className={containerClasses} data-component={componentID}>
            <ThirdPartyEmbed
                embedUrl={embedUrl || ""}
                embedTitle={title}
                additionalQueryParams={additionalQueryParams}
            />
        </div>
    );
}
